import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  addInventoryUnitsResponseSchema,
  inventoryResponseSchema,
  receiveKitsResponseSchema,
  stockLevelResponseSchema,
  templatesResponseSchema,
  type FormTemplate,
  type InventorySummary,
} from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { apiPost } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { useClaimRoles } from '../auth/useClaimRoles.js';
import { ApiError } from '../../components/ApiError.js';
import { ActionError } from '../../components/ActionError.js';
import { itemName } from './itemName.js';
import { STOREROOM_ROLES } from '../../components/RequireRole.js';

const input =
  'rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 focus:border-slate-500 focus:outline-none';
const primary =
  'rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40';
const secondary =
  'rounded-lg px-3 py-1.5 text-sm text-slate-600 ring-1 ring-slate-300 transition hover:bg-slate-100 disabled:opacity-40';

/**
 * The inventory dashboard (REQUIREMENTS §2.6): per catalog item — total,
 * available, issued, in rotation, lost/damaged. Quartermaster and admin get
 * the two write actions inline — register serials, adjust stock — and, above
 * the table, "receive kits": a form template's lines into stock, N times.
 */
export function InventoryPage() {
  const { t, i18n } = useTranslation();
  const { roles } = useClaimRoles();
  const canEdit = roles.some((r) => STOREROOM_ROLES.includes(r));
  const state = useApi('/inventory', inventoryResponseSchema);
  const [receiving, setReceiving] = useState(false);
  const [controlledOnly, setControlledOnly] = useState(false);

  if (state.kind === 'loading') return <p className="text-slate-500">{t('home.loading')}</p>;
  if (state.kind === 'error') return <ApiError code={state.code} requestId={state.requestId} />;

  const rows = state.data.items
    .filter((r) => r.item.active)
    .filter((r) => !controlledOnly || r.item.controlled);

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">{t('inventory.title')}</h2>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={controlledOnly}
              onChange={(e) => setControlledOnly(e.target.checked)}
              data-testid="inventory-controlled-only"
            />
            {t('inventory.controlledOnly')}
          </label>
          {canEdit && (
            <div className="flex flex-wrap items-baseline gap-4">
              <button
                type="button"
                onClick={() => setReceiving((v) => !v)}
                className="text-sm text-slate-500 underline-offset-4 hover:underline"
                data-testid="receive-kits-toggle"
              >
                {t('inventory.receiveKits')}
              </button>
              <Link
                to="/catalog"
                className="text-sm text-slate-500 underline-offset-4 hover:underline"
              >
                {t('inventory.manageCatalog')}
              </Link>
            </div>
          )}
        </div>
      </div>

      {canEdit && receiving && (
        <div className="mt-4">
          <ReceiveKitsForm
            rows={rows}
            language={i18n.language}
            onDone={() => {
              setReceiving(false);
              state.reload();
            }}
          />
        </div>
      )}

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="inventory-table">
          <thead>
            <tr className="border-b border-slate-200 text-xs text-slate-500">
              <th className="py-2 text-start font-medium">{t('inventory.columns.item')}</th>
              <th className="py-2 text-end font-medium">{t('inventory.columns.total')}</th>
              <th className="py-2 text-end font-medium">{t('inventory.columns.available')}</th>
              <th className="py-2 text-end font-medium">{t('inventory.columns.issued')}</th>
              <th className="py-2 text-end font-medium">{t('inventory.columns.inRotation')}</th>
              <th className="py-2 text-end font-medium">{t('inventory.columns.lostDamaged')}</th>
              {canEdit && (
                <th className="py-2 ps-6 text-start font-medium">
                  {t('inventory.columns.actions')}
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <InventoryRow
                key={r.item.id}
                row={r}
                canEdit={canEdit}
                language={i18n.language}
                onChanged={state.reload}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Num({ value, warn = false }: { value: number; warn?: boolean }) {
  return (
    <td className={`py-2 text-end ${warn && value > 0 ? 'text-amber-700' : 'text-slate-700'}`}>
      <span className="latin-inline">{value}</span>
    </td>
  );
}

function InventoryRow({
  row,
  canEdit,
  language,
  onChanged,
}: {
  row: InventorySummary;
  canEdit: boolean;
  language: string;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<'units' | 'stock' | null>(null);
  const serialized = row.item.tracking === 'serialized';
  const quantity = row.item.tracking === 'quantity';

  return (
    <>
      <tr data-testid={`inventory-${row.item.id}`}>
        <td className="py-2 text-slate-900">
          {serialized ? (
            <Link
              to={`/inventory/units?catalogItemId=${row.item.id}`}
              className="underline-offset-4 hover:underline"
            >
              {itemName(row.item, language)}
            </Link>
          ) : (
            itemName(row.item, language)
          )}
          <span className="ms-2 text-xs text-slate-400">
            {t(`inventory.tracking.${row.item.tracking}`)} ·{' '}
            {t(`inventory.ownership.${row.item.ownership}`)}
          </span>
          {row.item.controlled && (
            <span
              className="ms-2 rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800 ring-1 ring-amber-200"
              data-testid={`controlled-badge-${row.item.id}`}
            >
              {t('catalog.flags.controlled')}
            </span>
          )}
        </td>
        <Num value={row.total} />
        <Num value={row.available} />
        <Num value={row.issued} />
        <Num value={row.inRotation} warn />
        <Num value={row.lost + row.damaged} warn />
        {canEdit && (
          <td className="py-2 ps-6 text-start whitespace-nowrap">
            {serialized && (
              <button
                type="button"
                onClick={() => setOpen(open === 'units' ? null : 'units')}
                className="text-xs text-slate-500 underline-offset-4 hover:underline"
                data-testid={`add-units-${row.item.id}`}
              >
                {t('inventory.registerUnits')}
              </button>
            )}
            {quantity && (
              <button
                type="button"
                onClick={() => setOpen(open === 'stock' ? null : 'stock')}
                className="text-xs text-slate-500 underline-offset-4 hover:underline"
                data-testid={`adjust-stock-${row.item.id}`}
              >
                {t('inventory.adjustStock')}
              </button>
            )}
          </td>
        )}
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className="pb-3">
            {open === 'units' ? (
              <RegisterUnitsForm
                catalogItemId={row.item.id}
                onDone={() => {
                  setOpen(null);
                  onChanged();
                }}
              />
            ) : (
              <AdjustStockForm
                catalogItemId={row.item.id}
                available={row.available}
                onDone={() => {
                  setOpen(null);
                  onChanged();
                }}
              />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/** One serial per line. Scan-friendly: a keyboard-wedge scanner types a serial and Enter. */
function RegisterUnitsForm({
  catalogItemId,
  onDone,
}: {
  catalogItemId: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const add = useAction((serialNumbers: string[]) =>
    apiPost('/inventory/units', addInventoryUnitsResponseSchema, { catalogItemId, serialNumbers }),
  );
  const serials = text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await add.run(serials)) onDone();
  };

  return (
    <form
      onSubmit={(e) => void submit(e)}
      className="rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200"
    >
      <label className="block text-xs text-slate-600">
        {t('inventory.serialsHint')}
        <textarea
          className={`${input} latin-inline mt-1 block w-full`}
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="serials-input"
          placeholder={'PRC-4480\nPRC-4481'}
        />
      </label>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="submit"
          disabled={add.busy || serials.length === 0}
          className={primary}
          data-testid="serials-submit"
        >
          {t('inventory.registerCount', { count: serials.length })}
        </button>
        <button type="button" onClick={onDone} className={secondary}>
          {t('org.cancel')}
        </button>
      </div>
      <div className="mt-2">
        <ActionError error={add.error} />
        {add.error?.details.reason === 'duplicate_serial' &&
          Array.isArray(add.error.details.serials) && (
            <p className="text-xs text-slate-600 latin-inline">
              {(add.error.details.serials as string[]).join(', ')}
            </p>
          )}
      </div>
    </form>
  );
}

function AdjustStockForm({
  catalogItemId,
  available,
  onDone,
}: {
  catalogItemId: string;
  available: number;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('');
  const adjust = useAction((body: { delta: number; reason: string }) =>
    apiPost(`/inventory/stock/${catalogItemId}`, stockLevelResponseSchema, body),
  );
  const n = Number(delta);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await adjust.run({ delta: n, reason })) onDone();
  };

  return (
    <form
      onSubmit={(e) => void submit(e)}
      className="flex flex-wrap items-end gap-2 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200"
    >
      <label className="text-xs text-slate-600">
        {t('inventory.delta')}
        <input
          className={`${input} latin-inline mt-1 block w-28`}
          inputMode="numeric"
          value={delta}
          onChange={(e) => setDelta(e.target.value)}
          data-testid="stock-delta"
          placeholder="+50 / -20"
        />
      </label>
      <label className="grow text-xs text-slate-600">
        {t('inventory.reason')}
        <input
          className={`${input} mt-1 block w-full`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="stock-reason"
        />
      </label>
      <button
        type="submit"
        disabled={adjust.busy || !Number.isInteger(n) || n === 0 || reason.trim().length < 3}
        className={primary}
        data-testid="stock-submit"
      >
        {t('inventory.apply')}
      </button>
      <button type="button" onClick={onDone} className={secondary}>
        {t('org.cancel')}
      </button>
      <div className="basis-full">
        <ActionError error={adjust.error} />
        {adjust.error?.details.reason === 'insufficient_stock' && (
          <p className="text-xs text-slate-600">
            {t('inventory.onlyAvailable', { count: available })}
          </p>
        )}
      </div>
    </form>
  );
}

/**
 * "Receive kits" (owner, 2026-09-13): pick a form template, say how many
 * kits arrived, and every quantity line lands in stock `quantity × kits`.
 * The preview below the picker is the arithmetic the server will do, so the
 * storeroom sees "200 magazines, 40 vests" before it commits; a serialized
 * line (a helmet) is listed as skipped, since serials are registered one by one.
 */
function ReceiveKitsForm({
  rows,
  language,
  onDone,
}: {
  rows: InventorySummary[];
  language: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const templates = useApi('/templates', templatesResponseSchema);
  const [templateId, setTemplateId] = useState('');
  const [kits, setKits] = useState('1');
  const [reason, setReason] = useState('');
  const receive = useAction((body: { templateId: string; kits: number; reason: string }) =>
    apiPost('/inventory/receive-kits', receiveKitsResponseSchema, body),
  );
  const n = Number(kits);
  const validCount = Number.isInteger(n) && n >= 1;
  const items = new Map(rows.map((r) => [r.item.id, r.item]));
  const template: FormTemplate | undefined =
    templates.kind === 'ready'
      ? templates.data.templates.find((x) => x.id === templateId)
      : undefined;
  const preview = template
    ? template.lines.map((l) => ({ item: items.get(l.catalogItemId), line: l }))
    : [];
  const quantityLines = preview.filter((p) => p.item?.tracking === 'quantity');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await receive.run({ templateId, kits: n, reason })) onDone();
  };

  if (templates.kind === 'ready' && templates.data.templates.length === 0) {
    return (
      <p
        className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600 ring-1 ring-slate-200"
        data-testid="receive-kits-empty"
      >
        {t('inventory.noTemplates')}{' '}
        <Link to="/templates" className="underline-offset-4 hover:underline">
          {t('templates.title')}
        </Link>
      </p>
    );
  }

  return (
    <form
      onSubmit={(e) => void submit(e)}
      className="rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200"
      data-testid="receive-kits-form"
    >
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-600">
          {t('inventory.kitTemplate')}
          <select
            className={`${input} mt-1 block`}
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            data-testid="receive-kits-template"
          >
            <option value="">{t('templates.choose')}</option>
            {templates.kind === 'ready' &&
              templates.data.templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </option>
              ))}
          </select>
        </label>
        <label className="text-xs text-slate-600">
          {t('inventory.kitCount')}
          <input
            className={`${input} latin-inline mt-1 block w-24`}
            inputMode="numeric"
            value={kits}
            onChange={(e) => setKits(e.target.value)}
            data-testid="receive-kits-count"
          />
        </label>
        <label className="grow text-xs text-slate-600">
          {t('inventory.reason')}
          <input
            className={`${input} mt-1 block w-full`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="receive-kits-reason"
          />
        </label>
        <button
          type="submit"
          disabled={
            receive.busy ||
            !template ||
            quantityLines.length === 0 ||
            !validCount ||
            reason.trim().length < 3
          }
          className={primary}
          data-testid="receive-kits-submit"
        >
          {t('inventory.receive')}
        </button>
        <button type="button" onClick={onDone} className={secondary}>
          {t('org.cancel')}
        </button>
      </div>
      {template && (
        <ul
          className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600"
          data-testid="receive-kits-preview"
        >
          {preview.map(({ item, line }, i) => (
            <li
              key={i}
              className={item?.tracking === 'serialized' ? 'line-through opacity-60' : ''}
            >
              {item ? itemName(item, language) : line.catalogItemId}{' '}
              {item?.tracking === 'serialized'
                ? t('inventory.kitSerializedSkipped')
                : t('inventory.kitLine', {
                    each: line.quantity,
                    total: line.quantity * (validCount ? n : 0),
                  })}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2">
        <ActionError error={receive.error} />
      </div>
    </form>
  );
}
